/**
 * useRecoilCallbackを使う理由
 * ドキュメンテーション: https://recoiljs.org/docs/api-reference/core/useRecoilCallback/
 *
 * > "Asynchronously read Recoil state without subscribing a React component to re-render if the atom or selector is
 * updated"
 *
 * 複雑なステート処理functionが多いので、useRecoilStateのwrite atomを使ったステート更新を行うと、バッチ処理されてしまい、余分なレンダー
 * も生んでしまう。ステート処理とコンポーネントのレンダーを分けたいので、useRecoilCallbackを使う。
 */
import produce, { castDraft } from 'immer';
import { useContext } from 'react';
import { useRecoilCallback, useRecoilState } from 'recoil';

import { MatchingFormat } from '../../../../generated/graphql';
import { deselectAllElementsInArray } from '../../../../utility/arrayHandler';
import formatDecimalPoints from '../../../../utility/formatDecimalPoints';
import { context as DependencyContext } from '../../../context/dependency';
import { AnyFieldValue, DynamicSchemaValue, FileIdsValue } from '../../../types/DynamicSchema';
import { ExtendedProject } from '../../../types/ExtendedProject';
import { FileReference } from '../../../types/Files';
import { InputtedCreativeFormat } from '../../../types/InputtedCreativeFormat';
import {
  editingProjectStateAtom,
  editingProjectStateDefault,
  editingProjectStateErrorsAtom,
} from '../atoms/editingProjectStateAtom';
import { UnknownCreationSetError, UnknownCreativeFormatError } from '../domain/errors';
import {
  getInitialSelectedInputtedPlaybackTime,
  getUpdatedInputtingCreativeFormatsFilter,
} from '../domain/helperFunctions';
import { inputtedCreativeFormatsConverter } from '../domain/inputtedCreativeFormats';
import { projectPropertyConverter } from '../domain/projectPropertyConverter';
import { validateState } from '../domain/stateValidator';
import { useAssetFiles } from './useAssetFiles';

export const useEditingProjectState = () => {
  const [state, setState] = useRecoilState(editingProjectStateAtom);
  const [errors, setErrors] = useRecoilState(editingProjectStateErrorsAtom);
  const {
    editProjectUsecase: { deleteAssetFile },
  } = useContext(DependencyContext);
  const { removeAssetFile } = useAssetFiles();

  const setTitle = (title: string) => {
    const updatedState = produce(state, (draft) => {
      draft.title = title;
    });

    setErrors(validateState(updatedState));
    setState(updatedState);
  };

  const initializeEditingProject = (project: ExtendedProject) => {
    const converter = projectPropertyConverter(project);
    const inputtingCreativeFormats = converter.inputtingCreativeFormats();

    const state = produce(editingProjectStateDefault, (draft) => {
      draft.title = project.title;
      draft.matchingFormat = project.matchingFormat;
      draft.assetCategoryValue = converter.assetCategoryValues();
      draft.creativeBriefValue = converter.creativeBriefValues() || null;
      draft.creationSet = project.creationSet ?? null;
      draft.inputtingCreativeFormats = castDraft(inputtingCreativeFormats);
      draft.inputtingCreativeFormatsFilter = getUpdatedInputtingCreativeFormatsFilter(inputtingCreativeFormats);
    });

    setErrors(validateState(state));
    setState(state);
  };

  const updateCreationSetAmount = useRecoilCallback(({ snapshot, set }) => async (amount: number): Promise<void> => {
    const snapshotState = await snapshot.getPromise(editingProjectStateAtom);
    const snapshotErrors = await snapshot.getPromise(editingProjectStateErrorsAtom);
    const inputtedCreationSet = snapshotState.creationSet;

    if (inputtedCreationSet == null) {
      set(
        editingProjectStateErrorsAtom,
        produce(snapshotErrors, (draft) => {
          draft.creationSetsErrors.push(new UnknownCreationSetError());
        }),
      );
      return;
    }

    const updatedCreationSet = produce(inputtedCreationSet, (draft) => {
      draft.amount = amount;
    });

    const updatedState = produce(snapshotState, (draft) => {
      draft.creationSet = updatedCreationSet;
    });

    set(editingProjectStateErrorsAtom, validateState(updatedState));
    set(editingProjectStateAtom, updatedState);
  });

  const updateCreationSetDeliveryDueDays = useRecoilCallback(
    ({ snapshot, set }) => async (days: number | null, expressDueDays: number): Promise<void> => {
      const snapshotState = await snapshot.getPromise(editingProjectStateAtom);
      const snapshotErrors = await snapshot.getPromise(editingProjectStateErrorsAtom);

      const creationSet = snapshotState.creationSet;
      if (creationSet == null) {
        set(
          editingProjectStateErrorsAtom,
          produce(snapshotErrors, (draft) => {
            draft.creationSetsErrors.push(new UnknownCreationSetError());
          }),
        );
        return;
      }

      const updatedCreationSet = produce(creationSet, (draft) => {
        draft.deliveryDueDays = days;
        draft.expressDueDays = expressDueDays;
      });

      const updatedState = produce(snapshotState, (draft) => {
        draft.creationSet = castDraft(updatedCreationSet);
      });

      set(editingProjectStateErrorsAtom, validateState(updatedState));
      set(editingProjectStateAtom, updatedState);
    },
  );

  const updateProjectMatchingFormat = (selected: MatchingFormat) => {
    const updatedState = produce(state, (draft) => {
      draft.matchingFormat = selected;
    });

    setErrors(validateState(updatedState));
    setState(updatedState);
  };

  const removeCreativeFormat = ({
    creativeFormatId,
    resetSelectedCreativeFormatsState,
  }: {
    creativeFormatId: string;
    resetSelectedCreativeFormatsState: () => void;
  }) => {
    const creationSet = state.creationSet;

    if (creationSet == null) {
      setErrors(
        produce(errors, (draft) => {
          draft.creationSetsErrors = [new UnknownCreationSetError()];
        }),
      );
      return;
    }

    const targetCreativeFormat = creationSet.creativeFormats.find(
      (creativeFormat) => (creativeFormat?.tmpid || creativeFormat?.id) === creativeFormatId,
    );

    if (targetCreativeFormat == null) {
      setErrors(
        produce(errors, (draft) => {
          draft.creationSetsErrors = [new UnknownCreativeFormatError(creativeFormatId)];
        }),
      );
      return;
    }

    // creativeFormatをcreationSets内のcreativeFormats arrayから除く
    const updatedCreativeFormats = creationSet.creativeFormats.filter(
      (creativeFormat) =>
        creativeFormat !== targetCreativeFormat &&
        creativeFormat.resizeBaseId !== (targetCreativeFormat.tmpid || targetCreativeFormat.id),
    );
    if (updatedCreativeFormats.length === 0) {
      resetSelectedCreativeFormatsState();
    }

    // 2. safely update creativeSets using the updated creativeFormats
    const updatedState = produce(state, (draft) => {
      draft.creationSet = {
        ...creationSet,
        creativeFormats: updatedCreativeFormats,
      };
    });

    setErrors(validateState(updatedState));
    setState(updatedState);
  };

  const updateCreativeFormatAspectRatioDisplaySize = ({
    creativeFormatId,
    width,
    height,
  }: {
    creativeFormatId: string;
    width: number;
    height: number;
  }) => {
    const errors: Array<Error> = [];

    // get inputted state from index: creationSetIndex
    const targetCreationSet = state.creationSet;

    if (targetCreationSet === null) {
      errors.push(new UnknownCreationSetError());
      return;
    }

    // get inputted creative format index from id: creativeFormatId
    // by getting the index first, we can use this value later when updating the array, and also get the target value
    // without iterating through the array again (if we use `.find(target)`
    const targetCreativeFormatIndex = targetCreationSet.creativeFormats.findIndex(
      (creativeFormat) => (creativeFormat.tmpid || creativeFormat.id) === creativeFormatId,
    );
    const targetCreativeFormat = targetCreationSet.creativeFormats[targetCreativeFormatIndex];
    if (targetCreativeFormatIndex === -1) {
      errors.push(new UnknownCreativeFormatError(creativeFormatId));
    }

    const updatedCreativeFormat = produce(targetCreativeFormat, (draft) => {
      if (!draft.attribute.aspectRatio) return;
      draft.attribute.aspectRatio.size = {
        width: formatDecimalPoints(width),
        height: formatDecimalPoints(height),
      };
    });

    const updatedCreationSet = produce(targetCreationSet, (draft) => {
      draft.creativeFormats.splice(targetCreativeFormatIndex, 1, updatedCreativeFormat);
    });

    const updatedState = produce(state, (draft) => {
      draft.creationSet = castDraft(updatedCreationSet);
    });

    setErrors(validateState(updatedState));
    setState(updatedState);
  };

  const selectInputtingCreativeFormats = ({ index, resizeBaseId }: { index: number; resizeBaseId?: string }) => {
    const targetInputtingCreativeFormat = state.inputtingCreativeFormats[index];

    const updatedInputtingCreativeFormats = produce(
      deselectAllElementsInArray(state.inputtingCreativeFormats),
      (draft) => {
        draft.splice(index, 1, {
          ...castDraft(targetInputtingCreativeFormat),
          selected: true,
          resizeBaseId: resizeBaseId || null,
        });
      },
    );

    const updatedState = produce(state, (draft) => {
      draft.inputtingCreativeFormats = castDraft(updatedInputtingCreativeFormats);
    });

    setState(updatedState);
  };

  const setInputtingCreativeFormatsFilterByCreativeType = (index: number) => {
    const targetInputtingCreativeFormatsFilterByCreativeType =
      state.inputtingCreativeFormatsFilter.creativeTypes[index];

    if (!targetInputtingCreativeFormatsFilterByCreativeType) return;

    const updatedInputtingCreativeFormatsFilterByCreativeType = produce(
      deselectAllElementsInArray(state.inputtingCreativeFormatsFilter.creativeTypes),
      (draft) => {
        draft.splice(index, 1, {
          ...targetInputtingCreativeFormatsFilterByCreativeType,
          selected: true,
        });
      },
    );

    const updatedState = produce(state, (draft) => {
      draft.inputtingCreativeFormatsFilter.creativeTypes = castDraft(
        updatedInputtingCreativeFormatsFilterByCreativeType,
      );
    });

    setErrors(validateState(updatedState));
    setState(updatedState);
  };

  const clearAllSelectedInputtingCreativeFormatsFilterByCreativeType = () => {
    const inputtingCreativeFormatsFilterByCreativeTypes = state.inputtingCreativeFormatsFilter.creativeTypes;
    // CreativeFormatsFilterに条件が既に何も含まれていない状態なら、dispatchを呼ばない
    if (inputtingCreativeFormatsFilterByCreativeTypes.length === 0) return;

    const updatedState = produce(state, (draft) => {
      draft.inputtingCreativeFormatsFilter.creativeTypes = inputtingCreativeFormatsFilterByCreativeTypes;
    });

    setErrors(validateState(updatedState));
    setState(updatedState);
  };

  const setInputtingCreativeFormatsFilterByNetworkAndPlacement = (index: number) => {
    const targetInputtingCreativeFormatsFilterByNetworkAndPlacement =
      state.inputtingCreativeFormatsFilter.networksAndPlacements[index];

    if (!targetInputtingCreativeFormatsFilterByNetworkAndPlacement) return;

    const updatedInputtingCreativeFormatsFilterByNetworkAndPlacement = produce(
      deselectAllElementsInArray(state.inputtingCreativeFormatsFilter.networksAndPlacements),
      (draft) => {
        draft.splice(index, 1, {
          ...targetInputtingCreativeFormatsFilterByNetworkAndPlacement,
          selected: true,
        });
      },
    );

    const updatedState = produce(state, (draft) => {
      draft.inputtingCreativeFormatsFilter.networksAndPlacements = castDraft(
        updatedInputtingCreativeFormatsFilterByNetworkAndPlacement,
      );
    });

    setErrors(validateState(updatedState));
    setState(updatedState);
  };

  const clearAllSelectedInputtingCreativeFormatsFilterByNetworkAndPlacement = () => {
    const inputtingCreativeFormatsFilterByNetworksAndPlacements =
      state.inputtingCreativeFormatsFilter.networksAndPlacements;
    // CreativeFormatsFilterに条件が既に何も含まれていない状態なら、dispatchを呼ばない
    if (inputtingCreativeFormatsFilterByNetworksAndPlacements.length === 0) return;

    const updatedInputtingCreativeFormatsFilterByNetworksAndPlacements = deselectAllElementsInArray(
      inputtingCreativeFormatsFilterByNetworksAndPlacements,
    );
    const updatedState = produce(state, (draft) => {
      draft.inputtingCreativeFormatsFilter.networksAndPlacements = castDraft(
        updatedInputtingCreativeFormatsFilterByNetworksAndPlacements,
      );
    });

    setErrors(validateState(updatedState));
    setState(updatedState);
  };

  const addInputtingCreativeFormats = (originalCreativeFormat?: InputtedCreativeFormat) => {
    const addingInputtedCreativeFormats = inputtedCreativeFormatsConverter(state).adding();

    if (state.creationSet !== null) {
      const targetCreationSet = state.creationSet;

      if (targetCreationSet == null) {
        setErrors(
          produce(errors, (draft) => {
            draft.creationSetsErrors = [new UnknownCreationSetError()];
          }),
        );
        return;
      }

      const updatedCreationSet = {
        ...targetCreationSet,
        creativeFormats: targetCreationSet.creativeFormats.concat(addingInputtedCreativeFormats),
      };

      const inputtingCreativeFormats = state.inputtingCreativeFormats.map((format) => {
        return {
          ...format,
          selected: false,
          selectedPlaybackTimes: getInitialSelectedInputtedPlaybackTime(
            format.creativeType.attribute.playbackTime,
            originalCreativeFormat?.attribute?.playbackTime?.seconds,
          ),
          resizeBaseId: null,
        };
      });

      const updatedState = produce(state, (draft) => {
        draft.creationSet = castDraft(updatedCreationSet);
        draft.inputtingCreativeFormats = castDraft(inputtingCreativeFormats);
        draft.inputtingCreativeFormatsFilter = getUpdatedInputtingCreativeFormatsFilter(inputtingCreativeFormats);
      });

      setErrors(validateState(updatedState));
      setState(updatedState);
    } else {
      const inputtingCreativeFormats = state.inputtingCreativeFormats.map((format) => ({
        ...format,
        selected: false,
        selectedPlaybackTimes: getInitialSelectedInputtedPlaybackTime(
          format.creativeType.attribute.playbackTime,
          originalCreativeFormat?.attribute?.playbackTime?.seconds,
        ),
        resizeBaseId: null,
      }));

      const updatedState = produce(state, (draft) => {
        draft.creationSet = {
          id: null,
          creativeFormats: addingInputtedCreativeFormats,
          amount: 1,
          deliveryDueDays: null,
        };
        draft.inputtingCreativeFormats = castDraft(inputtingCreativeFormats);
        draft.inputtingCreativeFormatsFilter = getUpdatedInputtingCreativeFormatsFilter(inputtingCreativeFormats);
      });

      setErrors(validateState(updatedState));
      setState(updatedState);
    }
  };

  const clearAllSelectedInputtingCreativeFormats = (originalCreativeFormat?: InputtedCreativeFormat) => {
    const inputtingCreativeFormats = state.inputtingCreativeFormats.map((format) => ({
      ...format,
      selected: false,
      selectedPlaybackTimes: getInitialSelectedInputtedPlaybackTime(
        format.creativeType.attribute.playbackTime,
        originalCreativeFormat?.attribute?.playbackTime?.seconds,
      ),
      resizeBaseId: null,
    }));

    const updatedState = produce(state, (draft) => {
      draft.inputtingCreativeFormats = castDraft(inputtingCreativeFormats);
      draft.inputtingCreativeFormatsFilter = getUpdatedInputtingCreativeFormatsFilter(inputtingCreativeFormats);
    });

    setState(updatedState);
  };

  const updateInputtingCreativeFormatSelectedPlaybackTime = ({
    index,
    secondsList,
  }: {
    index: number;
    secondsList: ReadonlyArray<number>;
  }) => {
    const targetInputtingCreativeFormat = state.inputtingCreativeFormats[index];
    if (targetInputtingCreativeFormat === undefined) return;

    const updatedSelectedPlaybackTimes = [...secondsList]
      .sort((a, b) => a - b)
      .map((seconds) => ({
        supportingPlaybackTimeId: targetInputtingCreativeFormat.playbackTime!.id,
        requiredOption: targetInputtingCreativeFormat.playbackTime!.availables.find(
          (available) => available.seconds === seconds,
        )!.requiredOption,
        seconds,
      }));

    const updatedInputtingCreativeFormats = produce(state.inputtingCreativeFormats, (draft) => {
      draft.splice(index, 1, {
        ...targetInputtingCreativeFormat,
        selectedPlaybackTimes: updatedSelectedPlaybackTimes,
      });
    });

    const updatedState = produce(state, (draft) => {
      draft.inputtingCreativeFormats = castDraft(updatedInputtingCreativeFormats);
    });

    setErrors(validateState(updatedState));
    setState(updatedState);
  };

  const deleteUnnecessaryAssetFiles = useRecoilCallback(
    ({ snapshot, set }) => async (
      project: ExtendedProject | null,
      assetFiles: ReadonlyArray<FileReference>,
    ): Promise<void> => {
      const snapshotState = await snapshot.getPromise(editingProjectStateAtom);
      const assetCategoryValue = snapshotState.assetCategoryValue;
      const sections = project?.assetCategoryDefinition?.sections;

      if (sections == null || assetCategoryValue == null) return;

      sections.forEach((section) => {
        const meta = section.meta;
        const isAllowLeaveToTheCreatorsField = section.fields.find((field) => field.type === 'SELECT_ONE');

        if (meta != null && !meta.isAllowLeaveToTheCreators) return;
        if (isAllowLeaveToTheCreatorsField == null) return;
        if (assetCategoryValue.values[isAllowLeaveToTheCreatorsField.key] !== 'unnecessary') return;

        const fieldsInTargetSection = section.fields.filter(
          (field) => field.type === 'FILE_IDS' || field.type === 'SINGLE_LINE_TEXT' || field.type === 'MULTI_LINE_TEXT',
        );

        fieldsInTargetSection.forEach(async (fieldInTarget) => {
          const fieldKey = fieldInTarget.key;

          if (fieldKey == null) return;

          if (fieldInTarget.type === 'FILE_IDS') {
            const targetFileIdsValue: FileIdsValue = assetCategoryValue.values[fieldKey] as FileIdsValue;
            const targetFiles = assetFiles.filter((file) => targetFileIdsValue?.some((fileId) => file.id === fileId));

            for (const file of targetFiles) {
              await deleteAssetFile({ fileReference: file, fieldKey, bucketId: project?.bucketId, removeAssetFile });
            }

            const updatedState = produce(snapshotState, (draft) => {
              if (draft.assetCategoryValue == null) return;
              draft.assetCategoryValue.values[fieldKey] = [];
            });

            set(editingProjectStateErrorsAtom, validateState(updatedState));
            set(editingProjectStateAtom, updatedState);
          } else {
            const updatedState = produce(snapshotState, (draft) => {
              if (draft.assetCategoryValue == null) return;
              draft.assetCategoryValue.values[fieldKey] = '';
            });

            set(editingProjectStateErrorsAtom, validateState(updatedState));
            set(editingProjectStateAtom, updatedState);
          }
        });
      });
    },
  );

  const updateCreativeBriefValue = (fieldKey: string, value: AnyFieldValue) => {
    const updatedState = produce(state, (draft) => {
      if (draft.creativeBriefValue == null) return;
      draft.creativeBriefValue.values[fieldKey] = value;
    });

    setErrors(validateState(updatedState));
    setState(updatedState);
  };

  const updateAssetCategoryValue = (assetCategoryValue: DynamicSchemaValue) => {
    const updatedState = produce(state, (draft) => {
      draft.assetCategoryValue = assetCategoryValue;
    });

    setErrors(validateState(updatedState));
    setState(updatedState);
  };

  return {
    initializeEditingProject,
    setTitle,
    removeCreativeFormat,
    updateCreationSetAmount,
    updateProjectMatchingFormat,
    updateCreationSetDeliveryDueDays,
    selectInputtingCreativeFormats,
    addInputtingCreativeFormats,
    setInputtingCreativeFormatsFilterByCreativeType,
    setInputtingCreativeFormatsFilterByNetworkAndPlacement,
    clearAllSelectedInputtingCreativeFormats,
    clearAllSelectedInputtingCreativeFormatsFilterByCreativeType,
    clearAllSelectedInputtingCreativeFormatsFilterByNetworkAndPlacement,
    updateCreativeFormatAspectRatioDisplaySize,
    updateInputtingCreativeFormatSelectedPlaybackTime,
    deleteUnnecessaryAssetFiles,
    updateCreativeBriefValue,
    updateAssetCategoryValue,
  };
};
